import { slicedToArray as _slicedToArray, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { Flex, Box, FormField, Label, AdornedInput, Adornment, Input, FormHelp, Paragraph, Text, Slider, StyledSliderThumb, SliderLabel } from '@zillow/constellation';
import { event } from '@zillow/universal-analytics';
import React, { useState, useRef } from 'react';

var Thumb = function Thumb(_ref) {
  var props = _ref.props,
    state = _ref.state,
    income = _ref.income;
  return /*#__PURE__*/React.createElement(StyledSliderThumb, _extends({
    marginBottom: "xl"
  }, props), income && /*#__PURE__*/React.createElement(SliderLabel, null, income.monthlyNetIncomePretax ? Math.round(state.value / income.monthlyNetIncomePretax * 100) + "%" : 'N/A'));
};
var RentAffordabilityCalculator = function RentAffordabilityCalculator(_ref2) {
  var monthlyNetIncome = _ref2.monthlyNetIncome,
    monthlyDebts = _ref2.monthlyDebts,
    monthlySavings = _ref2.monthlySavings,
    monthlyExpenses = _ref2.monthlyExpenses;
  var _useState = useState({
      monthlyNetIncomePretax: 11600,
      monthlyNetIncome: monthlyNetIncome,
      monthlyDebts: monthlyDebts,
      monthlySavings: monthlySavings,
      monthlyExpenses: monthlyExpenses
    }),
    _useState2 = _slicedToArray(_useState, 2),
    incomeAndDebt = _useState2[0],
    setIncomeAndDebt = _useState2[1];
  var _useState3 = useState(Math.round(incomeAndDebt.monthlyNetIncomePretax * 0.33)),
    _useState4 = _slicedToArray(_useState3, 2),
    rent = _useState4[0],
    setRent = _useState4[1];
  var _useState5 = useState({
      monthlyNetIncome: '',
      monthlyDebts: '',
      monthlySavings: '',
      monthlyExpenses: ''
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    errors = _useState6[0],
    setErrors = _useState6[1];
  var overBudget = incomeAndDebt.monthlyNetIncome - rent - incomeAndDebt.monthlyDebts - incomeAndDebt.monthlySavings - incomeAndDebt.monthlyExpenses < 0;
  var originalValuesRef = useRef({});
  var storeLatestPrefocusedValueOnFocus = function storeLatestPrefocusedValueOnFocus(e, label) {
    originalValuesRef.current[label] = e.target.value;
  };
  var handleBlurAndSendEventIfModified = function handleBlurAndSendEventIfModified(e, label) {
    var oldVal = originalValuesRef.current[label];
    var newVal = e.target.value;
    if (newVal !== oldVal) {
      sendInteractionEvent(label);
    }
    if (!newVal) {
      e.target.value = '0';
      switch (label) {
        case 'Monthly net income':
          updateIncomeAndDebt(e, 'monthlyNetIncome');
          break;
        case 'Monthly debts':
          updateIncomeAndDebt(e, 'monthlyDebts');
          break;
      }
    }
  };
  var sendInteractionEvent = function sendInteractionEvent(label) {
    var cmsInfo = {
      block_type_cd: 'form_field',
      block_id: 'rent_afffordability_calc',
      block_displayed_txt: label
    };
    var newLaneData = {
      envelope: {
        event_type_id: '2056',
        event_type_version_id: '2',
        event_template_id: '126',
        event_template_version_id: '1'
      },
      clickstream_trigger: {
        trigger_type_nm: 'interaction',
        trigger_location_nm: 'cms',
        trigger_source_nm: 'cms_form',
        trigger_object_nm: 'no_trigger_object'
      },
      semantic: {
        semantic_event_nm: 'cms_engagement'
      },
      cms_info: cmsInfo
    };
    event(newLaneData);
  };
  var updateRent = function updateRent(e) {
    setRent(e);
  };
  var updateIncomeAndDebt = function updateIncomeAndDebt(e, type) {
    var updatedIncomeAndDebt = {};
    var updatedNetIncome;
    var updatedNetIncomePretax;
    var expenseValueValidity = '';
    var expenseValue = parseFloat(e.target.value);
    if (type === 'monthlyNetIncome') {
      if (!expenseValue && expenseValue !== 0) {
        expenseValueValidity = 'Please enter valid income value';
        updatedNetIncome = 0;
        updatedNetIncomePretax = 0;
      } else {
        updatedNetIncome = expenseValue;
        updatedNetIncomePretax = Math.round(updatedNetIncome / 0.75);
      }
      updatedIncomeAndDebt = {
        monthlyNetIncomePretax: updatedNetIncomePretax,
        monthlyNetIncome: updatedNetIncome
      };
      setRent(Math.max(100, Math.round(updatedNetIncomePretax * 0.33)));
    }
    if (type === 'monthlyDebts' || type === 'monthlySavings' || type === 'monthlyExpenses') {
      var _updatedIncomeAndDebt;
      if (!expenseValue && expenseValue !== 0) {
        expenseValueValidity = 'Please enter valid expense value';
      }
      updatedIncomeAndDebt = (_updatedIncomeAndDebt = {}, _updatedIncomeAndDebt[type] = expenseValue || 0, _updatedIncomeAndDebt);
    }
    setIncomeAndDebt(function (prevState) {
      return Object.assign({}, prevState, updatedIncomeAndDebt);
    });
    setErrors(function (prevState) {
      var _Object$assign;
      return Object.assign({}, prevState, (_Object$assign = {}, _Object$assign[type] = expenseValueValidity, _Object$assign));
    });
  };
  var sliderMin = Math.max(100, Math.round((incomeAndDebt.monthlyNetIncomePretax || 0) * 0.1));
  var sliderMax = Math.max(sliderMin + 100, Math.round((incomeAndDebt.monthlyNetIncomePretax || 0) * 0.4));
  var sliderStep = Math.max(1, Math.round((sliderMax - sliderMin) / 30));
  var handleSliderChange = function handleSliderChange(newValue) {
    setRent(newValue);
    sendInteractionEvent('Rent Affordability slider');
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Flex, {
    display: "flex",
    flexDirection: {
      default: 'column',
      lg: 'row'
    },
    gap: "lg",
    justifyItems: "center",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Flex, {
    flexShrink: 0,
    order: {
      default: 1,
      lg: 0
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: '382px'
    }
  }, /*#__PURE__*/React.createElement(Flex, {
    display: "flex",
    flexDirection: "column",
    gap: "sm"
  }, /*#__PURE__*/React.createElement(FormField, {
    label: /*#__PURE__*/React.createElement(Label, {
      required: true
    }, "Monthly net income"),
    error: errors.monthlyNetIncome !== '',
    control: /*#__PURE__*/React.createElement(AdornedInput, {
      leftAdornment: /*#__PURE__*/React.createElement(Adornment, null, "$"),
      input: /*#__PURE__*/React.createElement(Input, {
        id: "income-post-tax",
        type: "number",
        placeholder: "Income after taxes",
        defaultValue: incomeAndDebt.monthlyNetIncome,
        maxLength: 10,
        onChange: function onChange(e) {
          updateIncomeAndDebt(e, 'monthlyNetIncome');
        },
        onFocus: function onFocus(e) {
          return storeLatestPrefocusedValueOnFocus(e, 'Monthly net income');
        },
        onBlur: function onBlur(e) {
          return handleBlurAndSendEventIfModified(e, 'Monthly net income');
        }
      })
    }),
    description: /*#__PURE__*/React.createElement(FormHelp, null, errors.monthlyNetIncome)
  }), /*#__PURE__*/React.createElement(FormField, {
    label: /*#__PURE__*/React.createElement(Label, {
      required: true
    }, "Monthly debts"),
    error: errors.monthlyDebts !== '',
    control: /*#__PURE__*/React.createElement(AdornedInput, {
      leftAdornment: /*#__PURE__*/React.createElement(Adornment, null, "$"),
      input: /*#__PURE__*/React.createElement(Input, {
        id: "monthly-debts",
        type: "number",
        placeholder: "Credit cards, loans, ...",
        defaultValue: incomeAndDebt.monthlyDebts,
        maxLength: 7,
        onChange: function onChange(e) {
          updateIncomeAndDebt(e, 'monthlyDebts');
        },
        onFocus: function onFocus(e) {
          return storeLatestPrefocusedValueOnFocus(e, 'Monthly debts');
        },
        onBlur: function onBlur(e) {
          return handleBlurAndSendEventIfModified(e, 'Monthly debts');
        }
      })
    }),
    description: /*#__PURE__*/React.createElement(FormHelp, null, errors.monthlyDebts)
  }), /*#__PURE__*/React.createElement(FormField, {
    label: /*#__PURE__*/React.createElement(Label, null, "Monthly Savings"),
    error: errors.monthlySavings !== '',
    control: /*#__PURE__*/React.createElement(AdornedInput, {
      leftAdornment: /*#__PURE__*/React.createElement(Adornment, null, "$"),
      input: /*#__PURE__*/React.createElement(Input, {
        id: "monthly-savings",
        type: "number",
        placeholder: "Savings, 401k, ...",
        defaultValue: incomeAndDebt.monthlySavings,
        maxLength: 7,
        onChange: function onChange(e) {
          updateIncomeAndDebt(e, 'monthlySavings');
        },
        onFocus: function onFocus(e) {
          return storeLatestPrefocusedValueOnFocus(e, 'Monthly savings');
        },
        onBlur: function onBlur(e) {
          return handleBlurAndSendEventIfModified(e, 'Monthly savings');
        }
      })
    }),
    description: /*#__PURE__*/React.createElement(FormHelp, null, errors.monthlySavings)
  }), /*#__PURE__*/React.createElement(FormField, {
    label: /*#__PURE__*/React.createElement(Label, null, "Monthly Expenses"),
    error: errors.monthlyExpenses !== '',
    control: /*#__PURE__*/React.createElement(AdornedInput, {
      leftAdornment: /*#__PURE__*/React.createElement(Adornment, null, "$"),
      input: /*#__PURE__*/React.createElement(Input, {
        id: "monthly-expenses",
        type: "number",
        placeholder: "Food, insurance, ...",
        defaultValue: incomeAndDebt.monthlyExpenses,
        maxLength: 7,
        onChange: function onChange(e) {
          updateIncomeAndDebt(e, 'monthlyExpenses');
        },
        onFocus: function onFocus(e) {
          return storeLatestPrefocusedValueOnFocus(e, 'Monthly expenses');
        },
        onBlur: function onBlur(e) {
          return handleBlurAndSendEventIfModified(e, 'Monthly expenses');
        }
      })
    }),
    description: /*#__PURE__*/React.createElement(FormHelp, null, errors.monthlyExpenses)
  })))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: '382px',
      '@media $lg_gte': {
        width: 'auto'
      }
    }
  }, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    display: "flex",
    justifyItems: "center",
    alignItems: "center",
    gap: "md"
  }, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    display: "flex",
    justifyItems: "center",
    alignItems: "center",
    gap: "md",
    fontType: "h4"
  }, overBudget ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Paragraph, {
    fontType: "h4"
  }, "You're over budget at"), /*#__PURE__*/React.createElement(Paragraph, {
    fontType: "h2",
    fontColor: "red600",
    marginBottom: "xl"
  }, "$" + rent.toLocaleString('en-US') + " /mo")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Paragraph, {
    fontType: "h4"
  }, "You can afford"), /*#__PURE__*/React.createElement(Paragraph, {
    fontType: "h2",
    fontColor: "backgroundDarkGreen",
    marginBottom: "xl"
  }, "$" + rent.toLocaleString('en-US') + " /mo"))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      width: '100%'
    }
  }, /*#__PURE__*/React.createElement(Text, {
    style: {
      flex: 1,
      textAlign: 'left',
      marginRight: 20
    }
  }, "10%"), /*#__PURE__*/React.createElement(Slider, {
    id: "rent-value-slider",
    className: "rent-value-slider",
    min: sliderMin,
    max: sliderMax,
    value: rent,
    step: sliderStep,
    onAfterChange: handleSliderChange,
    onChange: function onChange(e) {
      return updateRent(e);
    },
    renderThumb: function renderThumb(props, state) {
      return /*#__PURE__*/React.createElement(Thumb, {
        props: props,
        state: state,
        income: incomeAndDebt
      });
    }
  }), /*#__PURE__*/React.createElement(Text, {
    style: {
      flex: 1,
      textAlign: 'right',
      marginLeft: 20
    }
  }, "40%")), overBudget ? /*#__PURE__*/React.createElement(Paragraph, {
    textAlign: "center"
  }, "Careful! If you spend this much on rent, your debts and expenses will exceed your income.") : /*#__PURE__*/React.createElement(Paragraph, {
    textAlign: "center"
  }, "Based on your income, a rental at this price should fit comfortably within your budget."), /*#__PURE__*/React.createElement(Paragraph, null, 'You will have ', /*#__PURE__*/React.createElement("strong", null, "$" + Math.round(incomeAndDebt.monthlyNetIncome - rent - incomeAndDebt.monthlyDebts - incomeAndDebt.monthlySavings - incomeAndDebt.monthlyExpenses).toLocaleString('en-US') + " /mo"), ' left to spend.'), /*#__PURE__*/React.createElement(Paragraph, {
    textAlign: "center",
    fontType: "finePrint"
  }, "DISCLAIMER: The calculated output is just a suggestion. All personal and financial factors should be considered before signing a lease.")))));
};
var RentAffordabilityCalculator$1 = RentAffordabilityCalculator;

export { RentAffordabilityCalculator$1 as default };
